import { useEffect, useState } from 'react'

import { contactSourceLabel } from 'src/graphql/types/contacts'
import { usDigits } from 'src/lib/phone.utils'

import {
  ContactChoiceSource,
  trackContactAdded,
  trackContactSkipped,
} from './contactTelemetry'
import { KnownContact } from './knownContacts'
import { useKnownCarrierContacts } from './useKnownCarrierContacts'

/**
 * Where the contact in an employment form came from, shared by the employment
 * details drawer (edit mode) and the Add employment drawer.
 *
 * State machine:
 * - `known_default`: the carrier-pick prefill wrote the directory's top contact
 *   into three blank fields. Shown as "Suggested · <source> · Clear".
 * - `known_chosen`: the recruiter picked a contact from the dropdown.
 * - either of those becomes `manual` once the fields no longer match what was
 *   written. That is derived, not tracked: every render compares the form's
 *   contact fields with the values the choice wrote (phones by digits, so the
 *   phone input's own reformatting does not count as an edit). No onChange
 *   wiring on three different inputs, and it cannot drift from the form.
 * - `clearSuggested` / `reset` drop the choice.
 *
 * A suggestion that still stands untouched is cleared when the DOT changes
 * underneath it, so a prefilled contact never outlives the employer it was
 * suggested for; a carrier pick then prefills again for the new DOT.
 */

export interface ContactFieldValues {
  contactName: string
  phone: string
  email: string
}

interface ContactChoiceState {
  source: Extract<ContactChoiceSource, 'known_default' | 'known_chosen'>
  contactId: string | null
  sourceLabel: string | null
  dotNumber: string
  applied: ContactFieldValues
}

const BLANK_CONTACT: ContactFieldValues = {
  contactName: '',
  phone: '',
  email: '',
}

const text = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : ''

// The phone input stores "+1(555) 123 - 4567" for what the directory holds as
// "5551234567"; those are the same number
const phoneKey = (value: unknown): string => usDigits(text(value))

const toFieldValues = (values: {
  contactName?: unknown
  phone?: unknown
  email?: unknown
}): ContactFieldValues => ({
  contactName: text(values.contactName),
  phone: text(values.phone),
  email: text(values.email),
})

export const isBlankContact = (values: {
  contactName?: unknown
  phone?: unknown
  email?: unknown
}): boolean =>
  !text(values.contactName) && !text(values.phone) && !text(values.email)

export const isSameContact = (
  a: { contactName?: unknown; phone?: unknown; email?: unknown },
  b: { contactName?: unknown; phone?: unknown; email?: unknown }
): boolean =>
  text(a.contactName) === text(b.contactName) &&
  phoneKey(a.phone) === phoneKey(b.phone) &&
  text(a.email).toLowerCase() === text(b.email).toLowerCase()

const fieldsFromContact = (contact: KnownContact): ContactFieldValues => ({
  contactName: contact.contactName || '',
  phone: contact.phoneNumber || '',
  email: contact.email || '',
})

export interface UseContactChoiceParams {
  /** Telemetry `component`, e.g. 'EmploymentDetailsDrawer'. */
  component: string
  dotNumber?: string | null
  /** The form's current contact fields. */
  values: { contactName?: unknown; phone?: unknown; email?: unknown }
  /** Writes all three contact fields into the form. */
  setContactFields: (fields: ContactFieldValues) => void
}

export const useContactChoice = ({
  component,
  dotNumber,
  values,
  setContactFields,
}: UseContactChoiceParams) => {
  const dot = text(dotNumber)
  const known = useKnownCarrierContacts(dot)
  const [choice, setChoice] = useState<ContactChoiceState | null>(null)
  const [pendingPrefillDot, setPendingPrefillDot] = useState<string | null>(
    null
  )

  const current = toFieldValues(values)
  const choiceIntact = choice ? isSameContact(current, choice.applied) : false
  const choiceSource: ContactChoiceSource | null = choice
    ? choiceIntact
      ? choice.source
      : 'manual'
    : null
  const suggestion =
    choice?.source === 'known_default' && choiceIntact ? choice : null

  const applyContact = (
    contact: KnownContact,
    source: ContactChoiceState['source']
  ) => {
    const fields = fieldsFromContact(contact)
    setContactFields(fields)
    setChoice({
      source,
      contactId: contact.contactId ?? null,
      sourceLabel: contactSourceLabel(contact.source)?.label ?? null,
      dotNumber: dot,
      applied: fields,
    })
  }

  useEffect(() => {
    // An untouched suggestion belongs to the DOT it was made for
    if (suggestion && suggestion.dotNumber !== dot) {
      setContactFields(BLANK_CONTACT)
      setChoice(null)
      return
    }

    if (!pendingPrefillDot) return
    // The DOT moved on before the contacts arrived
    if (pendingPrefillDot !== dot) {
      setPendingPrefillDot(null)
      return
    }
    if (known.loading) return

    setPendingPrefillDot(null)
    // The recruiter typed something in the meantime: theirs wins
    if (!isBlankContact(current)) return

    const top = known.contacts[0]
    if (top) applyContact(top, 'known_default')
    // setContactFields / applyContact are recreated every render by design;
    // the values they act on are all listed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dot,
    pendingPrefillDot,
    known.loading,
    known.contacts,
    suggestion,
    current.contactName,
    current.phone,
    current.email,
  ])

  /** Call after a carrier pick set the DOT: fills blank contact fields with the top contact. */
  const requestPrefill = (pickedDotNumber?: string | null) => {
    const picked = text(pickedDotNumber == null ? '' : String(pickedDotNumber))
    setPendingPrefillDot(picked || null)
  }

  /** A contact picked from the dropdown. */
  const selectKnownContact = (contact?: KnownContact | null) => {
    if (!contact) return
    setPendingPrefillDot(null)
    applyContact(contact, 'known_chosen')
  }

  /** "Clear" on the suggestion hint: empties the three fields. */
  const clearSuggested = () => {
    setContactFields(BLANK_CONTACT)
    setChoice(null)
    setPendingPrefillDot(null)
  }

  const reset = () => {
    setChoice(null)
    setPendingPrefillDot(null)
  }

  /**
   * Works out the voe_contact_* event for a save now, while the form still
   * holds what is being saved, and returns it to be emitted once the save
   * succeeds.
   *
   * - fields blank → voe_contact_skipped
   * - a choice is set → voe_contact_added with its (possibly downgraded) source
   * - otherwise, fields differ from `stored` (or there is none) → 'manual'
   * - otherwise unchanged → nothing
   */
  const prepareSaveTelemetry = (
    stored?: {
      contactName?: unknown
      phone?: unknown
      email?: unknown
    } | null
  ): (() => void) => {
    const snapshot = { ...current }
    const knownCount = known.count
    const dotAtSave = dot || null

    if (isBlankContact(snapshot)) {
      return () =>
        trackContactSkipped({ component, knownCount, dotNumber: dotAtSave })
    }

    if (!choiceSource && stored && isSameContact(snapshot, stored)) {
      return () => {}
    }
    const source: ContactChoiceSource = choiceSource ?? 'manual'

    return () =>
      trackContactAdded({
        component,
        source,
        contact: {
          contactName: snapshot.contactName,
          phoneNumber: snapshot.phone,
          email: snapshot.email,
        },
        knownCount,
        dotNumber: dotAtSave,
      })
  }

  return {
    knownContacts: known.contacts,
    knownCount: known.count,
    knownContactsLoading: known.loading,
    contactChoice: choiceSource
      ? { source: choiceSource, contactId: choice?.contactId ?? null }
      : null,
    /** Spread onto FormContactField for the hint line under the input. */
    contactHintProps: {
      // Undefined while loading, so the hint does not flash "No contacts"
      knownCount: dot && !known.loading ? known.count : undefined,
      suggested: Boolean(suggestion),
      suggestedSourceLabel: suggestion?.sourceLabel ?? null,
      onClearSuggested: clearSuggested,
    },
    requestPrefill,
    selectKnownContact,
    clearSuggested,
    reset,
    prepareSaveTelemetry,
  }
}
